"use strict";

import get from "lodash/get";
import { AuthService } from "customer-sdk/auth";
import { CrmService } from "customer-sdk/crm";

// Proxies
import AuthAccountLinkingProxy from "@/proxies/back-end/authentication/auth-account-linking-proxy";
import AuthAccountRegistrationProxy from "@/proxies/back-end/authentication/auth-account-registration-proxy";
import AuthLoginProxy from "@/proxies/back-end/authentication/auth-login-proxy";
import AuthChallengeProxy from "@/proxies/back-end/authentication/auth-challenge-proxy";
import AuthCodeVerifyProxy from "@/proxies/back-end/authentication/auth-code-verify-proxy";
import AuthRegisterProxy from "@/proxies/back-end/authentication/auth-register-proxy";
import AuthSessionVerificationProxy from "@/proxies/back-end/authentication/auth-session-verification-proxy";
import GetUsersMeProxy from "@/proxies/back-end/users/me";
import MfaChallengeProxy from "@/proxies/back-end/mfa/mfa-challenge-proxy";
import MfaVerifyProxy from "@/proxies/back-end/mfa/mfa-verify-proxy";

// Utilities
import {
  LocalStoreManager,
  logEvent,
  QueryStringHelper,
  LoginProxy,
  Auth0Util,
  isPartner,
  getAppFeatureProp,
} from "@sleek/customer-common-acquisition";

import { Constants } from "customer-sdk/constants";
const AUTH_CODES = Constants.AUTH_CODES;
const PAGE_URLS = Constants.PAGE_URLS;

import router from "@/routes";

import { KEYS } from "./constants";
import { storeUserProfile, getStorage, setSbaToken, setAuthKey } from "./utils";

export const actions = {
  async authenticate({ commit, state, dispatch }, { username, password }) {
    try {
      state.authenticationError = null;
      state.isAuthenticating = true;
      const proxy = new AuthLoginProxy();
      const response = await proxy.execute({
        username,
        password,
      });
      await dispatch("parseResponse", response);
    } catch (error) {
      console.error(error);
      state.authenticationError = error;
      commit("setErrorReference", error);
      commit("setAuthErrorMessage", "Authentication Error.");
    } finally {
      state.isAuthenticating = false;
    }
  },
  authFailureHandler({ state, dispatch }, error) {
    state.authenticationError = String(error);
    setTimeout(async () => {
      state.isAuthFailedRedirecting = true;
      await dispatch("redirectBack");
    }, 1500);
  },
  async changeRegistrationEmail({ state, commit }, email) {
    try {
      state.changeRegistrationEmailError = null;
      state.isChangingRegistrationEmail = true;
      if (state.registrationToken) {
        const proxy = new AuthAccountRegistrationProxy();
        const response = await proxy.changeEmail(
          state.registrationToken,
          email
        );
        commit("setVerificationProfile", response);
        commit("setRegistrationToken", response);
      }
    } catch (error) {
      state.changeRegistrationEmailError = String(error);
    } finally {
      state.isChangingRegistrationEmail = false;
    }
  },
  async handleLoginRedirect({ dispatch }, profile) {
    const oauthRedirectPath = await dispatch("getOauthRedirectPath");
    if (oauthRedirectPath) {
      dispatch("deleteOauthRedirectPath");
      return dispatch("waitToRedirect", oauthRedirectPath);
    }

    const queryStringHelper = new QueryStringHelper();
    const redirect = queryStringHelper.getRedirectUrlFriendly("redirect");

    if ((profile || {}).isPartnerAdmin) {
      return dispatch("waitToRedirect", PAGE_URLS.PARTNER.INDEX);
    } else if (redirect) {
      const invitationToken =
        queryStringHelper.getRedirectUrlFriendly("invitationToken");
      const isFromKycResubmissionRequest =
        queryStringHelper.getRedirectUrlFriendly(
          "isFromKycResubmissionRequest"
        );
      if (invitationToken && isFromKycResubmissionRequest === "true") {
        return dispatch(
          "waitToRedirect",
          `${PAGE_URLS.MEMBER_INVITATION}?invitation_token=${invitationToken}`
        );
      }
      return dispatch("waitToRedirect", redirect);
    } else {
      // const routeEngine = new DefaultRouteEngine();
      // const redirectPath = await routeEngine.execute(null, { isFromLogin: true });
      // return dispatch("waitToRedirect", redirectPath);
      return dispatch("waitToRedirect", PAGE_URLS.HOME);
    }
  },
  async initPkceAuthentication({ dispatch }, options = {}) {
    const { scope, callbackPath } = options;
    const proxy = new AuthChallengeProxy();
    const response = await proxy.execute({
      scope,
      callback_path: callbackPath,
    });
    return await dispatch("parseResponse", response);
  },
  async initializeLogin({ dispatch, state }) {
    try {
      await dispatch("persistOauthToken");
      const localStore = new LocalStoreManager();
      localStore.setToken(state.authToken);
      const loginProxy = new LoginProxy();
      const userResponse = await loginProxy.getUserContext(state.authToken);
      await storeUserProfile(userResponse.data);
      state.isAuthSuccessRedirecting = true;
      await dispatch("handleLoginRedirect");
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async loadSSOProfile({ state, dispatch }) {
    let response = null;
    try {
      state.isLoadingSSOProfile = true;
      const authToken = await dispatch("getPersistedOauthToken");
      if (authToken) {
        const proxy = new LoginProxy();
        response = await proxy.getUserContext(authToken, false);
        if (response) {
          const localStorage = new LocalStoreManager();
          await localStorage.storeProfile(null, response.data);
          localStorage.setItem("isLogin", true);
          localStorage.setToken(authToken);
          state.isSSOProfileLoaded = true;
        } else {
          state.isSSOProfileLoaded = false;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      state.isLoadingSSOProfile = false;
    }

    return response;
  },
  // eslint-disable-next-line max-lines-per-function,complexity
  async parseResponse({ commit, state, dispatch }, response) {
    commit("setMfaNonceIfAvailable", response);
    commit("setMfaExpiration", response);
    commit("setMfaRemainingAttempts", response);
    commit("setMfaNextResend", response);
    const code = get(response, "data.code");
    switch (code) {
      case AUTH_CODES.AUTH_SUCCESS:
        state.isPendingAccountLinking = false;
        state.validMfaCode = true;
        setSbaToken(response);
        dispatch("captureLoginEvent", response);
        commit("setAuthToken", get(response, "data.metadata.auth_token"));
        dispatch(
          "sendNewSessionToHubspot",
          get(response, "data.metadata.profile", {})
        );
        await dispatch("initializeLogin");
        break;
      case AUTH_CODES.AUTH_PENDING_ACCOUNT_CREATION:
        state.isPendingAccountCreation = true;
        commit("setRegistrationToken", response);
        commit("setPendingAccountProfile", response);
        break;
      case AUTH_CODES.AUTH_PENDING_ACCOUNT_LINKING:
        state.isPendingAccountLinking = true;
        commit("setPendingAccountLinkingProfile", response);
        commit("setAccountLinkingToken", response);
        break;
      case AUTH_CODES.AUTH_ACCOUNT_LINKING_INVALID_PASSWORD:
        commit("setAccountLinkingInvalidPassword", true);
        break;
      case AUTH_CODES.AUTH_CHALLENGED:
        state.authKey = get(response, "data.metadata.auth_key");
        state.authRedirectUri = get(response, "data.metadata.redirect_uri");
        if (state.authKey && state.authRedirectUri) {
          setAuthKey(state.authKey);
          dispatch("waitToRedirect", state.authRedirectUri);
        } else {
          throw new Error("Unable to get expected response");
        }
        break;
      case AUTH_CODES.AUTH_REAUTHENTICATE:
        await Auth0Util.initUniversalLogin({
          prompt: get(response, "data.metadata.prompt"),
          idToken: get(response, "data.metadata.id_token"),
        });
        break;
      case AUTH_CODES.MFA_REQUIRED:
        state.isMfaRequired = true;
        commit("setMfaToken", response);
        commit("setMfaPreferredChannel", response);
        commit("setMfaUserDetails", response);
        break;
      case AUTH_CODES.MFA_INVALID_CODE:
        state.invalidMfaCode = true;
        commit("setAuthErrorMessage", "Invalid OTP");
        break;
      case AUTH_CODES.MFA_TEMPORARY_BLOCKED:
        commit("setMfaTemporaryBlockedTime", response);
        state.isTemporaryBlocked = true;
        break;
      case AUTH_CODES.MFA_BLOCKED:
        state.isBlocked = true;
        break;
      case AUTH_CODES.MFA_ALREADY_USED:
        commit("setAuthErrorMessage", "Invalid OTP. Retry to login again");
        break;
      case AUTH_CODES.MFA_CHALLENGE_SUCCESS:
        // Success do nothing
        break;
      case AUTH_CODES.AUTH_PENDING_ACCOUNT_VERIFICATION:
        state.isPendingAccountVerification = true;
        commit("setVerificationProfile", response);
        commit("setRegistrationToken", response);
        dispatch("sendNewSessionToHubspot", {
          email: get(response, "data.metadata.email"),
          first_name: get(response, "data.metadata.first_name"),
          last_name: get(response, "data.metadata.last_name"),
        });
        break;
      case AUTH_CODES.ACCOUNT_VERIFICATION_SUCCESS_AND_LOGIN_REQUIRED:
        state.verifiedLoginRequired = true;
        break;
      default:
        commit("setSomethingWentWrongMessage");
        break;
    }
  },
  async persistOauthToken({ dispatch, state }) {
    const persistFlag = await dispatch("getTokenPeristenceFlag");
    if (String(persistFlag).toLowerCase() === "true") {
      const storage = getStorage();
      storage.setItem(KEYS.OAUTH_TOKEN_KEY, state.authToken);
    }
  },
  async redirectBack({ dispatch }) {
    const oauthRedirectPath = await dispatch("getOauthRedirectPath");
    if (oauthRedirectPath) {
      dispatch("deleteOauthRedirectPath");
      return await dispatch("waitToRedirect", oauthRedirectPath);
    } else {
      return await dispatch("waitToRedirect", PAGE_URLS.LOGIN);
    }
  },
  async requestMfaChallenge({ state, commit, dispatch }, channel) {
    if (state.isRequestingChallenge) return;
    commit("clearMfaErrors");
    if (state.mfaToken && state.nonce) {
      try {
        state.isRequestingChallenge = true;
        const preferredChannel = channel || state.preferredChannel;
        const proxy = new MfaChallengeProxy();
        const response = await proxy.execute({
          mfa_token: state.mfaToken,
          nonce: state.nonce,
          channel: preferredChannel,
        });
        await dispatch("parseResponse", response);
        if (preferredChannel !== state.preferredChannel) {
          state.preferredChannel = preferredChannel;
        }
        state.isRequestingChallenge = false;
      } catch (error) {
        state.challengeError = String(error);
        commit("setErrorReference", error);
        commit("setSomethingWentWrongMessage");
        throw error;
      }
    }
  },
  async registerOauthAccount({ state, dispatch }) {
    if (
      state.registrationToken &&
      state.pendingAccountProfile &&
      state.pendingAccountProfile.first_name &&
      state.pendingAccountProfile.last_name
    ) {
      state.registrationError = null;
      try {
        state.isOngoingRegistration = true;
        state.isAccountRegistration = true;
        const proxy = new AuthRegisterProxy();
        const response = await proxy.execute({
          registration_token: state.registrationToken,
          first_name: state.pendingAccountProfile.first_name,
          last_name: state.pendingAccountProfile.last_name,
        });
        await dispatch("parseResponse", response);
        state.isPendingAccountCreation = false;
      } catch (error) {
        state.registrationError = String(error);
      } finally {
        state.isOngoingRegistration = false;
      }
    }
  },
  async registerAccount({ state, dispatch }, { formData, registrationRef }) {
    try {
      state.isOngoingRegistration = true;
      state.isAccountRegistration = true;
      state.registrationError = null;
      state.registrationRef = registrationRef;
      const proxy = new AuthAccountRegistrationProxy();
      const response = await proxy.register(formData);
      await dispatch("parseResponse", response);
    } catch (error) {
      state.registrationError = error;
    } finally {
      state.isOngoingRegistration = false;
    }
  },
  async resendAccountVerification({ state, commit }) {
    try {
      state.resendVerificationError = null;
      state.isResendingVerification = true;
      if (state.registrationToken) {
        const proxy = new AuthAccountRegistrationProxy();
        const response = await proxy.resendVerification(
          state.registrationToken
        );
        commit("setVerificationProfile", response);
        commit("setRegistrationToken", response);
      }
    } catch (error) {
      state.resendVerificationError = String(error);
    } finally {
      state.isResendingVerification = false;
    }
  },
  async sendNewSessionToHubspot(
    { state, dispatch, getters },
    { first_name, last_name, email, onboardingReason }
  ) {
    try {
      const partner = await isPartner();
      if (email && !partner) {
        const service = new CrmService();
        const onboardingVersion = await dispatch("getOnboardingVersion");
        const payload = {
          email: email,
          firstname: first_name,
          lastname: last_name,
        };

        if (state.isAccountRegistration) {
          payload.app_onboarding_reason = getters.onboardingReason;
          return await service.submitAppOnboardingForm(payload);
        }
        if (onboardingVersion) {
          payload.app_onboarding_version = onboardingVersion;
          return await service.submitIncorporationFunnelForm(payload);
        }
        if (onboardingReason) {
          payload.app_onboarding_reason = onboardingReason;
          return await service.submitAppOnboardingForm(payload);
        }
        return await service.submitSiteLoginForm(payload);
      }
    } catch (e) {
      console.error("Error while sendNewSessionToHubspot", e);
    }
  },
  /**
   * initialize social login
   * @param {*} param0
   * @param {Object} payload
   * @param {String} payload.connection - Google / Facebook / Etc
   * @param {Object=} payload.options
   * @param {String=} payload.options.redirectPath - Redirect path. default to "/main/"
   * @param {String=} payload.options.onboardingVersion - Onboarding version
   */
  async socialLogin(
    { state, dispatch, rootGetters },
    { connection, options = {} } = {}
  ) {
    try {
      state.isRedirectingSocialSignIn = true;
      const {
        redirectPath = PAGE_URLS.HOME,
        persistToken = false,
        onboardingVersion = null,
      } = options;
      if (persistToken) {
        dispatch("setTokenPersistenceFlag");
      }
      dispatch("setOauthRedirectPath", redirectPath);
      if (onboardingVersion) {
        dispatch("setOnboardingVersion", onboardingVersion);
      }
      const platformConfig = rootGetters["configModule/platformConfig"];
      const auth0Config = getAppFeatureProp(
        platformConfig.cmsAppFeatures,
        "auth0"
      );
      const authService = new AuthService();
      await authService.initLoginRedirect({
        prompt: "select_account",
        connection: connection,
        domain: auth0Config.value.domain,
        clientId: auth0Config.value.clientId,
        audience: auth0Config.value.audience,
        callbackPath: "/auth/callback",
      });
      state.isOngoingSocialSignIn = true;
    } catch (error) {
      state.socialSignInError = String(error);
    } finally {
      state.isRedirectingSocialSignIn = false;
    }
  },
  async verifyAccount({ state, dispatch }, verificationToken) {
    try {
      state.isVerifyingAccount = true;
      const proxy = new AuthAccountRegistrationProxy();
      const response = await proxy.verifyAccount(verificationToken);
      await dispatch("parseResponse", response);
    } catch (error) {
      state.verifyAccountError = error;
    } finally {
      state.isVerifyingAccount = false;
    }
  },
  async verifyAccountLinking({ state, dispatch }, password) {
    state.accountLinkingError = null;
    if (state.accountLinkingToken) {
      try {
        state.isOngoingAccountLinking = true;
        const proxy = new AuthAccountLinkingProxy();
        const response = await proxy.execute({
          linking_token: state.accountLinkingToken,
          password: password,
        });
        await dispatch("parseResponse", response);
      } catch (error) {
        console.error(error);
        state.accountLinkingError = String(error);
      } finally {
        state.isOngoingAccountLinking = false;
      }
    }
  },
  async verifyAuthSession({ state, dispatch }, oauth) {
    try {
      state.isVerifyingSession = true;
      const { access_token, id_token, refresh_token, token_type } = oauth;
      const proxy = new AuthSessionVerificationProxy();
      const response = await proxy.execute({
        token_type: token_type,
        access_token: access_token,
        refresh_token: refresh_token,
        id_token: id_token,
      });
      await dispatch("parseResponse", response);
    } catch (error) {
      state.sessionVerifyError = String(error);
    } finally {
      state.isVerifyingSession = false;
    }
  },
  async verifyCurrentSession({ state }) {
    try {
      state.isVerifyingSession = true;
      const localStore = new LocalStoreManager();
      const authToken = localStore.getToken();
      if (!authToken) {
        localStore.logout();
        state.isVerifyingSession = false;
        return;
      }

      const proxy = new GetUsersMeProxy();
      const currentUser = await proxy.getWithoutAuthCheck(authToken);
      if (currentUser) {
        state.currentUser = currentUser;
        state.isSessionVerified = true;
      } else {
        // User session expired, need to clear all previous session reference
        localStore.logout();
      }
    } catch (error) {
      state.sessionVerifyError = String(error);
    } finally {
      state.isVerifyingSession = false;
    }
  },
  async verifyMfaCode({ state, commit, dispatch }, mfaCode) {
    commit("clearMfaErrors");
    if (state.mfaToken && state.nonce) {
      try {
        state.isVerifyingMfaCode = true;
        const proxy = new MfaVerifyProxy();
        const response = await proxy.execute({
          mfa_token: state.mfaToken,
          nonce: state.nonce,
          code: mfaCode,
        });
        await dispatch("parseResponse", response);
      } catch (error) {
        state.verifyMfaError = String(error);
        commit("setErrorReference", error);
        commit("setSomethingWentWrongMessage");
      } finally {
        state.isVerifyingMfaCode = false;
      }
    }
  },
  async verifyPkceCode(
    { state, dispatch },
    { code, code_verifier, redirect_uri }
  ) {
    try {
      state.authenticationError = null;
      state.isAuthenticating = true;
      const proxy = new AuthCodeVerifyProxy();
      const response = await proxy.execute({
        code: code,
        code_verifier: code_verifier,
        redirect_uri: redirect_uri,
      });
      await dispatch("parseResponse", response, true);
    } catch (error) {
      console.error(error);
      state.authenticationError = String(error);
      setTimeout(async () => {
        state.isAuthFailedRedirecting = true;
        await dispatch("redirectBack");
      }, 1500);
    } finally {
      state.isAuthenticating = false;
    }
  },
  cancelSocialLogin({ state, dispatch, commit }) {
    if (state.socialSigninWindow) {
      state.socialWindowManuallyClosed = true;
      state.socialSigninWindow.close();
      state.socialSigninWindow = null;
    }
    dispatch("clearSocialListener");
    commit("clearSocialSignInStatus");
  },
  captureLoginEvent({ rootGetters }, response) {
    try {
      const userId = get(response, "data.metadata.profile.user_id");
      if (userId) {
        logEvent.handleWriteToMixpanel("$user_id", userId, "set");
        logEvent.handleWriteToMixpanel("Application Login", {
          "User ID": userId,
        });
        logEvent.handleWriteToMixpanel("Page View", {
          "Page Name": "Application Login",
        });
        logEvent.handleWriteToMixpanel(null, userId, "identify");
        const platformConfig = rootGetters["configModule/platformConfig"];
        if (platformConfig) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "Login",
            formId: `Sleek_${platformConfig.tenant}_login`,
            userId: userId,
          });
        }
      }
    } catch (error) {
      console.error("Failed to capture login event");
      console.error(error);
    }
  },
  clearSocialListener({ state }) {
    if (state.socialListener) {
      clearInterval(state.socialListener);
      state.socialListener = null;
    }
  },
  deleteOauthRedirectPath() {
    const storage = getStorage();
    storage.removeItem(KEYS.OAUTH_REDIRECT_PATH_KEY);
  },
  deleteOauthPeristedToken() {
    const storage = getStorage();
    storage.removeItem(KEYS.OAUTH_TOKEN_KEY);
  },
  getOauthRedirectPath() {
    const storage = getStorage();
    return storage.getItem(KEYS.OAUTH_REDIRECT_PATH_KEY);
  },
  getOnboardingVersion() {
    const storage = getStorage();
    const version = storage.getItem(KEYS.APP_ONBOARDING_VERSION_KEY);
    if (version) {
      storage.removeItem(KEYS.APP_ONBOARDING_VERSION_KEY);
      return version;
    }
    return null;
  },
  getPersistedOauthToken() {
    const storage = getStorage();
    const token = storage.getItem(KEYS.OAUTH_TOKEN_KEY);
    if (token) {
      storage.removeItem(KEYS.OAUTH_TOKEN_KEY);
      return JSON.parse(token);
    }
    return null;
  },
  getTokenPeristenceFlag() {
    const storage = getStorage();
    const flag = storage.getItem(KEYS.OAUTH_TOKEN_PERISTENCE_KEY);
    if (flag) {
      storage.removeItem(KEYS.OAUTH_TOKEN_PERISTENCE_KEY);
      return flag;
    }

    return false;
  },
  listenToSocialWindow({ state, dispatch }, callback) {
    // check if client manually closed the tab
    dispatch("clearSocialListener");
    state.socialListener = setInterval(() => {
      if (!state.socialSigninWindow || state.socialSigninWindow.closed) {
        const fallbackError = !state.socialSigninWindow
          ? new Error("Failed to open new tab")
          : new Error("The browser tab was closed");
        callback({
          error: state.socialWindowManuallyClosed ? null : fallbackError,
        });
        dispatch("clearSocialListener");
      }
    }, 1500);
  },
  // eslint-disable-next-line id-length
  setOauthRedirectPath(_, redirectPath) {
    const storage = getStorage();
    storage.setItem(KEYS.OAUTH_REDIRECT_PATH_KEY, redirectPath);
  },
  // eslint-disable-next-line id-length
  setOnboardingVersion(_, onboardingVersion) {
    const storage = getStorage();
    storage.setItem(KEYS.APP_ONBOARDING_VERSION_KEY, onboardingVersion);
  },
  setTokenPersistenceFlag() {
    const storage = getStorage();
    storage.setItem(KEYS.OAUTH_TOKEN_PERISTENCE_KEY, "true");
  },
  waitToRedirect({ commit }, path = PAGE_URLS.LOGIN) {
    if (path[path.length - 1] === "&") {
      path = path.slice(0, -1);
    }
    const fallbackRedirect = () => {
      setImmediate(() => {
        window.location.href = path;
      });
      commit("resetState");
    };
    setTimeout(async () => {
      try {
        if (!path.startsWith("http")) {
          await router.push(path);
          commit("resetState");
        } else {
          fallbackRedirect();
        }
      } catch (error) {
        fallbackRedirect();
      }
    }, 600);
  },
};
